import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
}

export interface Invoice {
  id: string;
  user_id: string;
  proposal_id: string | null;
  industry: string;
  invoice_number: string;
  status: "draft" | "sent" | "paid" | "overdue" | "void";
  client_name: string;
  client_company: string | null;
  client_email: string | null;
  client_address: string | null;
  issue_date: string;
  due_date: string | null;
  line_items: InvoiceLineItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount: number;
  total: number;
  amount_paid: number;
  notes: string | null;
  payment_terms: string | null;
  paid_at: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export type InvoiceInput = Omit<Invoice, "id" | "user_id" | "created_at" | "updated_at" | "paid_at" | "sent_at">;

export function calcInvoiceTotals(items: InvoiceLineItem[], taxRate: number, discount: number) {
  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax_amount = ((subtotal - discount) * (Number(taxRate) || 0)) / 100;
  const total = Math.max(0, subtotal - discount + tax_amount);
  return { subtotal, tax_amount, total };
}

export function useInvoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchInvoices = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    const { data, error } = await supabase
      .from("invoices")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast({ title: "Failed to load invoices", description: error.message, variant: "destructive" });
    setInvoices((data as any[] as Invoice[]) || []);
    setIsLoading(false);
  }, [user]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const saveInvoice = async (input: Partial<InvoiceInput> & { id?: string }) => {
    if (!user) return null;
    const { id, ...payload } = input;
    if (id) {
      const { data, error } = await supabase.from("invoices").update(payload as any).eq("id", id).select().single();
      if (error) { toast({ title: "Save failed", description: error.message, variant: "destructive" }); return null; }
      await fetchInvoices();
      return data as unknown as Invoice;
    } else {
      const { data, error } = await supabase.from("invoices").insert({ ...(payload as any), user_id: user.id }).select().single();
      if (error) { toast({ title: "Save failed", description: error.message, variant: "destructive" }); return null; }
      await fetchInvoices();
      return data as unknown as Invoice;
    }
  };

  const deleteInvoice = async (id: string) => {
    const { error } = await supabase.from("invoices").delete().eq("id", id);
    if (error) { toast({ title: "Delete failed", description: error.message, variant: "destructive" }); return; }
    await fetchInvoices();
  };

  const markPaid = async (id: string, total: number) => {
    const { error } = await supabase.from("invoices").update({ status: "paid", amount_paid: total, paid_at: new Date().toISOString() }).eq("id", id);
    if (error) { toast({ title: "Update failed", description: error.message, variant: "destructive" }); return; }
    await fetchInvoices();
  };

  const markSent = async (id: string) => {
    await supabase.from("invoices").update({ status: "sent", sent_at: new Date().toISOString() }).eq("id", id);
    await fetchInvoices();
  };

  return { invoices, isLoading, fetchInvoices, saveInvoice, deleteInvoice, markPaid, markSent };
}

export function nextInvoiceNumber(existing: Invoice[]): string {
  const year = new Date().getFullYear();
  const prefix = `INV-${year}-`;
  const nums = existing
    .map((i) => i.invoice_number)
    .filter((n) => n?.startsWith(prefix))
    .map((n) => parseInt(n.slice(prefix.length), 10))
    .filter((n) => !isNaN(n));
  const next = (nums.length ? Math.max(...nums) : 0) + 1;
  return `${prefix}${String(next).padStart(4, "0")}`;
}
